import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, API_URL } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import { ArrowLeft, Download, Printer, Share2, Mail, MessageCircle, Edit, Link2, Copy, Eye as EyeIcon } from "lucide-react";

export default function QuotationPreview() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/quotations/${id}`);
      setQ(data);
      loadPdf();
    })();
    // eslint-disable-next-line
  }, [id]);

  const loadPdf = async () => {
    try {
      const token = localStorage.getItem("traveloble_token");
      const ctrl = new AbortController();
      const tout = setTimeout(() => ctrl.abort(), 60000);
      const res = await fetch(`${API_URL}/quotations/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: ctrl.signal,
      });
      clearTimeout(tout);
      if (!res.ok) {
        let msg = "PDF generation failed";
        try {
          const body = await res.json();
          if (body?.detail) msg = `PDF generation failed: ${body.detail}`;
        } catch {}
        toast.error(msg);
        return;
      }
      const blob = await res.blob();
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      toast.error(e?.name === "AbortError" ? "PDF rendering timed out" : "PDF generation failed");
    }
  };

  const download = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${q?.quotation_number || "quotation"}.pdf`;
    a.click();
  };

  const printPdf = () => {
    if (!pdfUrl) return;
    const w = window.open(pdfUrl);
    if (w) setTimeout(() => w.print(), 800);
  };

  const shareWA = () => {
    const url = publicUrl || window.location.href;
    const text = encodeURIComponent(`Your travel quotation ${q?.quotation_number} is ready — ${q?.destination?.country} • ${q?.total_nights}N/${q?.total_days}D\n\n${url}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareEmail = () => {
    const subj = encodeURIComponent(`Travel Quotation ${q?.quotation_number}`);
    const link = publicUrl ? `\n\nView online: ${publicUrl}` : "";
    const body = encodeURIComponent(`Dear ${q?.lead?.name || "Traveler"},\n\nPlease find your ${q?.destination?.country} package quotation.${link}\n\nWarm regards,\n${q?.package_title || ""}`);
    window.location.href = `mailto:${q?.lead?.email || ""}?subject=${subj}&body=${body}`;
  };

  const createShare = async () => {
    try {
      const { data } = await api.post(`/quotations/${id}/share`);
      const url = `${window.location.origin}${data.share_path}`;
      setPublicUrl(url);
      setShowShare(true);
      toast.success("Public link created");
    } catch (e) { toast.error("Could not create share link"); }
  };

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  };

  if (!q) return <div className="p-10">Loading…</div>;

  return (
    <div className="flex min-h-screen">
      <Sidebar role="agent" />
      <main className="flex-1 bg-slate-100 p-6 lg:p-10">
        <div className="flex items-center justify-between max-w-[1200px] mb-6">
          <div>
            <button data-testid="preview-back-btn" onClick={()=>nav(-1)} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-600 hover:text-[#16A34A]"><ArrowLeft size={13}/> Back</button>
            <h1 className="font-editorial text-3xl mt-2 text-slate-900">{q.package_title || `${q.destination?.country || ""} Package`}</h1>
            <div className="text-xs text-slate-500 mt-1 font-mono-e">{q.quotation_number}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link data-testid="preview-edit-btn" to={`/quotations/${id}/edit`} className="inline-flex items-center gap-1 px-3.5 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]"><Edit size={13}/> Edit</Link>
            <button data-testid="preview-share-btn" onClick={createShare} className="inline-flex items-center gap-1 px-3.5 py-2 bg-[#0F1E5B] text-white rounded text-xs uppercase tracking-widest hover:bg-[#16A34A] transition-colors"><Link2 size={13}/> Get share link</button>
            <button data-testid="preview-print-btn" onClick={printPdf} className="inline-flex items-center gap-1 px-3.5 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]"><Printer size={13}/> Print</button>
            <button data-testid="preview-download-btn" onClick={download} className="inline-flex items-center gap-1 px-3.5 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]"><Download size={13}/> Download</button>
            <button data-testid="preview-wa-btn" onClick={shareWA} className="inline-flex items-center gap-1 px-3.5 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]"><MessageCircle size={13}/> WhatsApp</button>
            <button data-testid="preview-email-btn" onClick={shareEmail} className="inline-flex items-center gap-1 px-3.5 py-2 border border-slate-300 rounded text-xs uppercase tracking-widest hover:border-[#16A34A]"><Mail size={13}/> Email</button>
          </div>
        </div>
        {showShare && publicUrl && (
          <div className="max-w-[1200px] mb-4 bg-[#BBF7D0]/40 border border-[#BBF7D0] rounded p-4 flex items-center gap-3">
            <Link2 size={16} className="text-[#16A34A]"/>
            <div className="flex-1 min-w-0">
              <div className="overline text-[9px] !text-[#16A34A]">Public link — no login required for viewer</div>
              <div data-testid="public-url-value" className="mt-0.5 text-sm font-mono-e truncate">{publicUrl}</div>
            </div>
            <button data-testid="copy-share-link-btn" onClick={copyLink} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 border border-[#16A34A] text-[#16A34A] rounded hover:bg-[#16A34A] hover:text-white transition-colors"><Copy size={12}/> Copy</button>
            <a data-testid="open-public-link" href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest px-3 py-1.5 border border-slate-300 rounded"><EyeIcon size={12}/> Open</a>
          </div>
        )}
        <div className="paper max-w-[1200px] mx-auto rounded overflow-hidden" style={{ minHeight: "80vh" }}>
          {pdfUrl ? (
            <iframe data-testid="pdf-iframe" src={pdfUrl} className="w-full" style={{ height: "85vh", border: 0 }} title="Quotation preview" />
          ) : (
            <div className="p-14 text-center text-slate-500">Rendering PDF…</div>
          )}
        </div>
      </main>
    </div>
  );
}
